import {
  Avatar,
  Box,
  Flex,
  HStack,
  Icon,
  Progress,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import {
  getCourseEnrolmentHealthByLabel,
  getProgressStatusText,
} from '@duke-hq/shared-constants/enrolments';
import { CourseEnrolmentWithUserAndCourseDetails } from '@duke-hq/svc-api/provider/services/courseService';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import { Baby, CircleX, GitPullRequestArrow, SquarePen, TriangleAlert } from 'lucide-react';
import { useCallback, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CopyToClipboardPopover } from '@duke-hq/ui/components/CopyToClipboardPopover/CopyToClipboardPopover';
import { SortOrder, parseSortOrder } from '@duke-hq/lib/pagination';
import { UpdateEnrolmentModal } from './UpdateEnrolment/UpdateEnrolmentModal';
import WithdrawEnrolmentModal from './WithdrawEnrolmentModal';
import RestoreEnrolmentModal from './RestoreEnrolmentModal';
import PauseEnrolmentModal from './PauseEnrolmentModal';
import { calculateEnrolmentMetrics } from '@duke-hq/svc-api/lib/enrolment/calculateEnrolmentMetrics';
import { isUnderMinimumAge } from '@duke-hq/shared-constants/students';
import { EnrolmentHealthBadge } from '@duke-hq/ui/components/EnrolmentHealthBadge/EnrolmentHealthBadge';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import EnrolmentDocumentsModal from '@duke-hq/enrolments-ui/components/EnrolmentDocumentsModal';
import { listAvailableEnrolmentDocuments } from '@duke-hq/svc-api/lib/enrolment/listAvailableEnrolmentDocuments';
import { FourteenDayPaceBadge } from '@duke-hq/enrolments-ui/components/FourteenDayPaceBadge';
import { useEnrolmentDocumentQuery } from '../lib/useEnrolmentDocumentQuery';
import { getUserFullName } from '@duke-hq/lib';
import { EnrolmentActionsMenu } from './EnrolmentActionsMenu';
import { CourseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import SupersededCourseStatusBadge from '@duke-hq/enrolments-ui/components/SupersededCourseStatusBadge';

function EnrolmentListRow({
  enrolment,
  getProviderName,
  onClick,
  onOpenUpdateModal,
  onOpenWithdrawModal,
  onOpenRestoreModal,
  onOpenPauseModal,
  onOpenDocumentsModal,
}: {
  enrolment: CourseEnrolmentWithUserAndCourseDetails;
  getProviderName: (providerId: number) => string;
  onClick?: (id: number) => void;
  onOpenUpdateModal: () => void;
  onOpenWithdrawModal: () => void;
  onOpenRestoreModal: () => void;
  onOpenPauseModal: () => void;
  onOpenDocumentsModal: () => void;
}): React.ReactElement {
  const fullName = getUserFullName({
    first_name: enrolment.user.first_name,
    last_name: enrolment.user.last_name,
  });

  const { currentSemester, expectedProgress, startDelay, targetDate } =
    calculateEnrolmentMetrics(enrolment);

  const progressHealthColorScheme = enrolment.progress_health
    ? getCourseEnrolmentHealthByLabel(enrolment.progress_health)?.colorScheme
    : undefined;

  const progressStatusText = getProgressStatusText(enrolment.progress_status);

  const isAtRisk = enrolment.progress_health
    ? ['At risk'].includes(enrolment.progress_health)
    : false;

  const isAtRiskOrOverdue = enrolment.progress_health
    ? ['At risk', 'Overdue'].includes(enrolment.progress_health)
    : false;

  const studentData = enrolment.user?.studentData;
  const hasInvalidUSI =
    studentData !== null &&
    studentData.usi_verified_at == null &&
    studentData.usi_verified_by == null;

  const hasCtOrRplRequest =
    (enrolment.applyForCreditTransfer === true && enrolment.ctResolvedAt == null) ||
    (enrolment.applyForRpl === true && enrolment.rplResolvedAt == null);

  const formattedSemester = useMemo(() => {
    if (enrolment?.course?.kind === CourseKindSchema.enum.SKILL_SET) {
      return <Text color="fg.muted">N/A</Text>;
    }

    if (currentSemester == null) {
      return <Trans>N/A</Trans>;
    }

    return djs()
      .date(currentSemester === 0 ? 1 : currentSemester)
      .format('Do');
  }, [enrolment?.course?.kind, currentSemester]);

  return (
    <Tr
      key={enrolment.id}
      cursor="pointer"
      onClick={() => {
        if (onClick) {
          onClick(enrolment.id);
        }
      }}
      _hover={{ bg: 'bg.subtle' }}
      bg={isAtRisk ? 'danger.muted' : undefined}
      borderLeftColor="red"
      borderLeftWidth={isAtRisk ? 4 : 0}
    >
      <Td>
        <HStack spacing="3">
          <Flex alignItems="center">
            <Avatar
              size="sm"
              mr={'12px'}
              name={fullName}
              src={enrolment.user?.picture_url ?? undefined}
            ></Avatar>
            <Box>
              <Text fontWeight="medium">{fullName}</Text>
              <HStack spacing="1">
                <Text fontWeight="normal" color="fg.muted">
                  {enrolment.user.email}
                </Text>
                {enrolment.user.secondaryEmail != null ? (
                  <CopyToClipboardPopover value={enrolment.user.secondaryEmail} />
                ) : null}
              </HStack>
            </Box>
          </Flex>
        </HStack>
      </Td>
      <Td style={{ maxWidth: '300px' }}>
        {enrolment.course ? (
          <VStack align="start" spacing={0} w="100%" maxW="100%">
            <Text isTruncated fontWeight="medium" title={enrolment.course.title} w="100%">
              {enrolment.course.title}
            </Text>
            <HStack spacing={2}>
              {enrolment.course.status === CourseStatusSchema.enum.SUPERSEDED ? (
                <SupersededCourseStatusBadge status={enrolment.course.status} />
              ) : null}
              {enrolment.course.identifier != null ? (
                <Text color="fg.muted">{enrolment.course.identifier}</Text>
              ) : null}
            </HStack>
          </VStack>
        ) : null}
      </Td>
      <Td>{formattedSemester}</Td>
      <Td>
        {progressStatusText ? (
          <Tag
            fontSize="xs"
            fontWeight="bold"
            borderRadius="full"
            colorScheme={progressStatusText.colorScheme}
            variant={progressStatusText.variant}
          >
            {progressStatusText.text}
          </Tag>
        ) : (
          <Flex gap="5px" alignItems="center">
            <Box position="relative" rounded={12} overflow="hidden" height="22px" width="100px">
              <Progress
                value={currentSemester === 0 ? 0 : enrolment.semesterProgress}
                rounded={12}
                height="100%"
                width="100%"
                sx={{
                  '& > div': {
                    background: 'blue.200',
                    rounded: 0,
                  },
                }}
                borderColor="blue.500"
                borderWidth={1}
              />

              <Box
                borderRight="1px"
                borderColor={isAtRiskOrOverdue ? 'red' : 'info'}
                borderStyle="dashed"
                position="absolute"
                top={0}
                height="22px"
                left={`${expectedProgress}%`}
              />
            </Box>

            <Text
              size="sm"
              fontWeight="semibold"
              color={isAtRiskOrOverdue ? `${progressHealthColorScheme}.500` : undefined}
            >
              {currentSemester === 0 ? 0 : enrolment.semesterProgress}%
            </Text>
          </Flex>
        )}
      </Td>
      <Td>
        <EnrolmentHealthBadge health={enrolment.health} />
      </Td>
      <Td>
        <FourteenDayPaceBadge
          pace={enrolment.recentPaceStatus}
          labels={{
            great: t`Great`,
            good: t`Good`,
            slow: t`Slow`,
            none: t`None`,
            na: t`N/A`,
          }}
        />
      </Td>
      <Td>
        {enrolment.user.last_activity_at ? (
          <Text color={isAtRisk ? 'red' : 'fg.muted'}>
            {djs().to(djs(enrolment.user.last_activity_at))}
          </Text>
        ) : (
          <Text color={isAtRisk ? 'red' : 'fg.muted'}>
            <Trans>Not logged in yet</Trans>
          </Text>
        )}
      </Td>
      <Td color={isAtRiskOrOverdue ? 'red' : 'fg.muted'}>
        {startDelay > 0 ? t`${startDelay} days` : <Trans>none</Trans>}
      </Td>
      <Td>{djs(enrolment.start_at_date).format('DD MMM YY')}</Td>
      <Td>{targetDate?.medium ?? <Trans>none</Trans>}</Td>
      <Td>
        {enrolment.tenant_id != null ? (
          <Text>{getProviderName(enrolment.tenant_id)}</Text>
        ) : (
          <Text color="fg.muted">N/A</Text>
        )}
      </Td>
      <Td>
        <Text>{enrolment.course_segment === 'Full course' ? 'Full course' : '1st semester'}</Text>
        <Text>{enrolment.work_schedule === 'FULL_TIME' ? 'Full-time' : 'Part-time'}</Text>
      </Td>
      <Td>
        {enrolment?.enrolment_contract_type ? (
          <Text>{enrolment.enrolment_contract_type}</Text>
        ) : (
          <Text color="fg.muted">N/A</Text>
        )}
      </Td>
      <Td>
        {enrolment.progress_health ? (
          <Tag
            fontSize="xs"
            fontWeight="bold"
            borderRadius="full"
            colorScheme={progressHealthColorScheme}
            variant="outline"
          >
            {enrolment.unit_lag}
          </Tag>
        ) : null}
      </Td>
      <Td>
        <HStack spacing="3">
          {hasInvalidUSI ? (
            <Tooltip hasArrow label={t`USI Missing / Incorrect`}>
              <Box as="span">
                <Icon as={CircleX} color={'danger'} boxSize="5" />
              </Box>
            </Tooltip>
          ) : null}

          {hasCtOrRplRequest ? (
            <Tooltip hasArrow label={t`CT / RPL requested`}>
              <Box as="span">
                <Icon as={GitPullRequestArrow} color={'warning'} boxSize="5" />
              </Box>
            </Tooltip>
          ) : null}

          {isUnderMinimumAge(enrolment.user?.studentData?.date_of_birth) && (
            <Tooltip hasArrow label={t`Under 16`}>
              <Box as="span">
                <Icon as={Baby} color={'warning'} boxSize="5" />
              </Box>
            </Tooltip>
          )}
        </HStack>
      </Td>
      <Td>
        <Text color="fg.muted">{djs().to(djs(enrolment.created_at))}</Text>
      </Td>
      <Td position="sticky" right={0} zIndex={1} bg="bg.surface" px={8}>
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          boxShadow="-4px 0px 6px rgba(0, 0, 0, 0.1)"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <EnrolmentActionsMenu
            enrolment={enrolment}
            variant="table"
            stopPropagation
            onViewEnrolment={() => onClick?.(enrolment.id)}
            onUpdateEnrolment={onOpenUpdateModal}
            onWithdrawEnrolment={onOpenWithdrawModal}
            onRestoreEnrolment={onOpenRestoreModal}
            onPauseResumeEnrolment={onOpenPauseModal}
            onViewDocuments={onOpenDocumentsModal}
          />
        </Box>
      </Td>
    </Tr>
  );
}
interface CourseEnrolmentProps {
  enrolments: CourseEnrolmentWithUserAndCourseDetails[];
  getProviderName: (providerId: number) => string;
}

export function EnrolmentsTable({
  enrolments = [],
  getProviderName,
}: CourseEnrolmentProps): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderBy = searchParams.get('orderBy') ?? 'undefined';
  const sort = parseSortOrder(searchParams.get('sort'));

  const [isUpdatingEnrolment, setIsUpdatingEnrolment] =
    useState<CourseEnrolmentWithUserAndCourseDetails | null>(null);

  const [isWithdrawEnrolment, setIsWithdrawEnrolment] =
    useState<CourseEnrolmentWithUserAndCourseDetails | null>(null);

  const [isRestoreEnrolment, setIsRestoreEnrolment] =
    useState<CourseEnrolmentWithUserAndCourseDetails | null>(null);

  const [isPauseEnrolment, setIsPauseEnrolment] =
    useState<CourseEnrolmentWithUserAndCourseDetails | null>(null);

  const [isEnrolmentDocument, setIsEnrolmentDocument] =
    useState<CourseEnrolmentWithUserAndCourseDetails | null>(null);

  const handleClick = useCallback(
    (id: string | number) => {
      navigate(`/enrolment/${id}?${searchParams.toString()}`);
    },
    [navigate, searchParams],
  );

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set('orderBy', name);
      newParams.set('sort', newSortDirection);
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, sort],
  );

  const getPdfDocumentDataFn = useEnrolmentDocumentQuery();

  return (
    <>
      <Table>
        <Thead>
          <Tr>
            <Th>
              <SortableHeader
                label={t`Student`}
                columnName="name"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`Course`}
                columnName="course_id"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <Trans>Semester</Trans>
            </Th>
            <Th>
              <Trans>Semester Progress</Trans>
            </Th>
            <Th>
              <SortableHeader
                label={t`Health`}
                columnName="health"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`14-day pace`}
                columnName="recentPaceStatus"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`Last activity`}
                columnName="last_activity_at"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <Trans>Start Delay</Trans>
            </Th>
            <Th>
              <SortableHeader
                label={t`Course Start`}
                columnName="start_at_date"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <Trans>Course Target</Trans>
            </Th>
            <Th>
              <SortableHeader
                label={t`Provider`}
                columnName="provider"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>
            <Th>
              <Flex justify="space-between" align="center">
                <HStack spacing="5">
                  <Text>Segment/Pace</Text>
                </HStack>
              </Flex>
            </Th>
            <Th>
              <SortableHeader
                label={t`Contract`}
                columnName="enrolment_contract_type"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>

            <Th>
              <SortableHeader
                label={t`Unit Lag`}
                columnName="unit_lag"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>

            <Th>
              <Box as="span">
                <Icon as={TriangleAlert} boxSize="4" />
              </Box>
            </Th>
            <Th>
              <SortableHeader
                label={t`Created`}
                columnName="created_at"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </Th>

            <Th position="sticky" right={0} zIndex={2} bg="white !important" px={8}>
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                boxShadow="-4px 0px 6px rgba(0, 0, 0, 0.1)"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <SquarePen width={20} height={20} />
              </Box>
            </Th>
          </Tr>
        </Thead>

        <Tbody>
          {Array.isArray(enrolments) && enrolments.length
            ? enrolments.map((enrolment: CourseEnrolmentWithUserAndCourseDetails) => (
                <EnrolmentListRow
                  key={enrolment.id}
                  enrolment={enrolment}
                  getProviderName={getProviderName}
                  onClick={handleClick}
                  onOpenUpdateModal={() => {
                    setIsUpdatingEnrolment(enrolment);
                  }}
                  onOpenWithdrawModal={() => {
                    setIsWithdrawEnrolment(enrolment);
                  }}
                  onOpenRestoreModal={() => {
                    setIsRestoreEnrolment(enrolment);
                  }}
                  onOpenPauseModal={() => {
                    setIsPauseEnrolment(enrolment);
                  }}
                  onOpenDocumentsModal={() => {
                    setIsEnrolmentDocument(enrolment);
                  }}
                />
              ))
            : null}
        </Tbody>
      </Table>

      {isUpdatingEnrolment ? (
        <UpdateEnrolmentModal
          enrolmentId={isUpdatingEnrolment.id}
          isOpen={isUpdatingEnrolment !== null}
          onClose={() => {
            setIsUpdatingEnrolment(null);
          }}
        />
      ) : null}

      {isWithdrawEnrolment != null ? (
        <WithdrawEnrolmentModal
          enrolment={isWithdrawEnrolment}
          isOpen={true}
          onClose={() => {
            setIsWithdrawEnrolment(null);
          }}
        />
      ) : null}

      {isRestoreEnrolment != null ? (
        <RestoreEnrolmentModal
          enrolmentId={isRestoreEnrolment.id}
          studentId={isRestoreEnrolment.user_id}
          isOpen={true}
          onClose={() => {
            setIsRestoreEnrolment(null);
          }}
        />
      ) : null}

      {isPauseEnrolment != null ? (
        <PauseEnrolmentModal
          enrolment={isPauseEnrolment}
          isOpen={true}
          onClose={() => {
            setIsPauseEnrolment(null);
          }}
        />
      ) : null}

      {isEnrolmentDocument != null ? (
        <EnrolmentDocumentsModal
          enrolment={isEnrolmentDocument}
          isOpen={true}
          onClose={() => {
            setIsEnrolmentDocument(null);
          }}
          availableDocuments={listAvailableEnrolmentDocuments({
            enrolment: isEnrolmentDocument,
            isAdmin: true,
            userUTCOffset: djs().utcOffset(),
          })}
          getPdfDocumentDataFn={getPdfDocumentDataFn}
          labels={{
            title: t`Enrolment Documents`,
            emptyMessage: t`This enrolment doesn't have any documents available at the moment.`,
            view: t`View`,
            done: t`Done`,
          }}
        />
      ) : null}
    </>
  );
}
